import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { AppForm } from '../../../../../Form/AppForm';
import { SystemIcon } from '../../../../../Icon/SystemIcon';
import { Typography } from '../../../../../Typography/Typography';
import { INITIAL_VALUES } from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.constants';
import {
  FileFormat,
  FormCreateScheduleExport,
  TimeInterval
} from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import {
  useTranslatedFileFormatOptions,
  useTranslatedTimeIntervalOptions
} from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/useTranslatedOptions';
import { useIntlUds } from '../../../../../../translations/UdsIntlProvider';
import { getAvailableTimeOptions } from './useScheduleExportForm';

interface FormProps {
  datePicker?: {
    helperText?: string;
    errorMessage?: string;
  };
}
interface Props {
  exportFileFormats: FileFormat[];
  timeIntervals?: TimeInterval[];
  setScheduleExport: (scheduleExport: FormCreateScheduleExport) => void;
  scheduleExport: Partial<FormCreateScheduleExport>;
  formProps?: FormProps;
}

export const ScheduleExportForm: FC<React.PropsWithChildren<Props>> = ({
  scheduleExport,
  exportFileFormats,
  timeIntervals,
  setScheduleExport,
  formProps = {},
  children
}) => {
  const { intl } = useIntlUds();
  const { hoursOptions, tomorrow, currentHour } = getAvailableTimeOptions(scheduleExport.startTimestamp);
  const { datePicker } = formProps;

  const filteredOptions = useTranslatedFileFormatOptions(exportFileFormats);
  const timeIntervalOptions = useTranslatedTimeIntervalOptions(timeIntervals);

  const onFormChange = useCallback(
    (newFormValues: FormCreateScheduleExport) => {
      const { hoursOptions, isTodaySelected } = getAvailableTimeOptions(newFormValues.startTimestamp);
      const wasTodaySelected = isTodaySelected && newFormValues.startTimestamp !== scheduleExport.startTimestamp;
      if ((newFormValues.startTimestamp && !newFormValues.hour) || wasTodaySelected) {
        setScheduleExport({ ...newFormValues, hour: hoursOptions[0]?.value });
      } else {
        setScheduleExport(newFormValues);
      }
    },
    [scheduleExport.startTimestamp, setScheduleExport]
  );

  return (
    <AppForm<Partial<FormCreateScheduleExport>>
      initialValues={scheduleExport ?? INITIAL_VALUES}
      onChange={onFormChange}
    >
      <StyledDateInputs>
        <AppForm.Control name="startTimestamp" required>
          <AppForm.Label>{intl.DataTable.ScheduleExport.Form.startDateInput.label}</AppForm.Label>
          <AppForm.DatePicker
            helperText={datePicker?.errorMessage ?? datePicker?.helperText}
            error={!!datePicker?.errorMessage}
            minDate={currentHour === 23 ? tomorrow : undefined}
            disablePast
            dataTest="start-date"
            value={scheduleExport.startTimestamp}
            fullWidth
          />
        </AppForm.Control>
        <AppForm.Control name="hour" required>
          <AppForm.Label>{intl.DataTable.ScheduleExport.Form.preferredTimeInput.label}</AppForm.Label>
          <AppForm.Select
            dataTest="hour"
            components={{
              PopupIcon: () => <SystemIcon className="clock-hour-icon" name="schedule" />
            }}
            className="hour-select"
            fullWidth
            disableClearable
            value={scheduleExport.hour}
            disabled={!scheduleExport.startTimestamp}
            options={hoursOptions}
          />
          <AppForm.HelperText>{intl.DataTable.ScheduleExport.Form.preferredTimeInput.helperText}</AppForm.HelperText>
        </AppForm.Control>
      </StyledDateInputs>
      <AppForm.Control name="timeInterval">
        <Typography variant="label1">{intl.DataTable.ScheduleExport.Form.timeIntervalInput.label}</Typography>
        <AppForm.ChipsSelect dataTest="time-interval" options={timeIntervalOptions} />
      </AppForm.Control>
      <AppForm.Control name="fileFormat">
        <AppForm.Label>{intl.DataTable.ScheduleExport.Form.fileFormatInput.label}</AppForm.Label>
        <AppForm.Select dataTest="file-format" disableClearable options={filteredOptions} />
      </AppForm.Control>
      {children}
    </AppForm>
  );
};

const StyledDateInputs = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacingSize.m};
`;
